/**
 * MFX OS — PO Inbox (blueprint Stage 1). Dedicated intake queue, not a file
 * list: every row shows client, matched quote, total, reviewer, age, status.
 * Intake channels here: manual/email log form + one-click import of POs
 * submitted through the website portal (quotes docs carrying po* fields).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useOsList, getOsDoc, usd, ago } from '../../os/core';
import { receiveCustomerPO } from '../../os/commands';
import { quoteClient, quoteEmail, quoteLabel, quoteTiers, type QuoteDoc } from '../../os/compare';
import { Chip, Modal } from '../../os/ui';
import type { CustomerPO, POLine, POChannel } from '../../os/types';
import { loadRecentQuotes, type RecentQuote } from '../../lib/osData';

const BUCKETS = ['all', 'REVIEW_REQUIRED', 'RECEIVED', 'WAITING_ON_CLIENT', 'EXCEPTION', 'APPROVED', 'REJECTED'] as const;

let LINE_SEQ = 1;
const newLine = (): POLine => ({ id: `l${Date.now()}-${LINE_SEQ++}`, customerPart: '', description: '', qty: 0, unitPrice: 0, total: 0, mappedTo: '', mapStatus: 'unmapped' });

export default function PoInbox() {
  const nav = useNavigate();
  const { rows, loading } = useOsList<CustomerPO>('customerPOs', { order: ['receivedAt', 'desc'] });
  const [bucket, setBucket] = useState<(typeof BUCKETS)[number]>('all');
  const [q, setQ] = useState('');
  const [openIntake, setOpenIntake] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);

  const shown = useMemo(() => {
    let r = rows;
    if (bucket !== 'all') r = r.filter((x) => x.status === bucket);
    if (q.trim()) { const t = q.toLowerCase(); r = r.filter((x) => `${x.poNumber} ${x.clientName} ${x.quoteNum}`.toLowerCase().includes(t)); }
    return r;
  }, [rows, bucket, q]);

  const counts = useMemo(() => ({
    review: rows.filter((x) => x.status === 'REVIEW_REQUIRED' || x.status === 'RECEIVED').length,
    waiting: rows.filter((x) => x.status === 'WAITING_ON_CLIENT').length,
    exception: rows.filter((x) => x.status === 'EXCEPTION').length,
    approved: rows.filter((x) => x.status === 'APPROVED').length,
  }), [rows]);

  if (loading) return <div className="loading"><div className="spinner" /> Loading PO inbox…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">PO Inbox</div>
          <div className="page-sub">Customer purchase orders — matched, validated, and promoted to sales orders. Nothing advances unchecked.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setPortalOpen(true)}>⇪ Import from portal</button>
          <button className="btn primary" onClick={() => setOpenIntake(true)}>＋ Log a PO</button>
        </div>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Needs review</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{counts.review}</div><div className="metric-note">received + review required</div></div>
        <div className="metric-card"><div className="metric-label">Waiting on client</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{counts.waiting}</div><div className="metric-note">clarifications out</div></div>
        <div className="metric-card"><div className="metric-label">Exceptions</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{counts.exception}</div><div className="metric-note">authorized deviations</div></div>
        <div className="metric-card"><div className="metric-label">Approved</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{counts.approved}</div><div className="metric-note">ready / promoted to SO</div></div>
      </div>

      <div className="toolbar">
        <input className="search" placeholder="Search PO #, client, quote…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="seg">{BUCKETS.map((b) => <button key={b} className={bucket === b ? 'on' : ''} onClick={() => setBucket(b)}>{b === 'all' ? 'All' : b.replace(/_/g, ' ').toLowerCase()}</button>)}</div>
      </div>

      <div className="table-wrap"><table>
        <thead><tr><th>PO</th><th>Client</th><th>Customer PO #</th><th>Matched quote</th><th>Total</th><th>Match</th><th>Reviewer</th><th>Age</th><th>Status</th></tr></thead>
        <tbody>
          {shown.map((x) => (
            <tr key={x.id} onClick={() => nav(`/os/po?id=${encodeURIComponent(x.id)}`)}>
              <td><strong>{x.displayId}</strong></td>
              <td>{x.clientName}</td>
              <td>{x.poNumber}</td>
              <td style={{ color: x.quoteId ? 'var(--tx)' : 'var(--rd)' }}>{x.quoteNum || 'not linked'}</td>
              <td>{x.total ? usd(x.total) : '—'}</td>
              <td>{x.comparison ? (x.comparison.blockers.length ? <span style={{ color: 'var(--rd)' }}>⛔ {x.comparison.blockers.length}</span> : <span style={{ color: 'var(--gn)' }}>✓ clean</span>) : '—'}</td>
              <td style={{ color: 'var(--tx2)' }}>{(x.reviewer || '').split('@')[0] || '—'}</td>
              <td>{ago(x.receivedAt)}</td>
              <td><Chip label={x.status} /></td>
            </tr>
          ))}
          {shown.length === 0 && <tr><td colSpan={9}><div className="empty">No POs in this bucket. Log one or import from the portal.</div></td></tr>}
        </tbody>
      </table></div>

      <IntakeModal open={openIntake} onClose={() => setOpenIntake(false)} onDone={(id) => { setOpenIntake(false); nav(`/os/po?id=${encodeURIComponent(id)}`); }} />
      <PortalImport open={portalOpen} onClose={() => setPortalOpen(false)} existing={rows} onDone={(id) => { setPortalOpen(false); nav(`/os/po?id=${encodeURIComponent(id)}`); }} />
    </>
  );
}

// ── manual intake ────────────────────────────────────────────────────────
function IntakeModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [f, setF] = useState({ poNumber: '', clientName: '', clientEmail: '', channel: 'manual' as POChannel, requestedDate: '', shipTo: '', paymentTerms: '', notes: '', fileUrl: '' });
  const [lines, setLines] = useState<POLine[]>([newLine()]);
  const [quotes, setQuotes] = useState<RecentQuote[]>([]);
  const [quoteId, setQuoteId] = useState('');
  const [qFilter, setQFilter] = useState('');

  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  const setL = (i: number, k: keyof POLine, v: string) => setLines((ls) => ls.map((l, j) => {
    if (j !== i) return l;
    const nl = { ...l, [k]: k === 'qty' || k === 'unitPrice' ? Number(v) || 0 : v } as POLine;
    nl.total = nl.qty * nl.unitPrice;
    return nl;
  }));

  const loadQuotes = async () => { if (!quotes.length) setQuotes(await loadRecentQuotes(200)); };
  const total = lines.reduce((s, l) => s + l.total, 0);
  const shownQuotes = quotes.filter((x) => !qFilter.trim() || `${x.quoteNum} ${x.customerName} ${x.sku}`.toLowerCase().includes(qFilter.toLowerCase())).slice(0, 12);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      let quote: QuoteDoc | null = null;
      if (quoteId) quote = (await getOsDoc<Record<string, unknown>>('quotes', quoteId)) as unknown as QuoteDoc | null;
      const id = await receiveCustomerPO({
        poNumber: f.poNumber, clientName: f.clientName, clientEmail: f.clientEmail, channel: f.channel,
        total: total || undefined, requestedDate: f.requestedDate || undefined, shipTo: f.shipTo || undefined,
        paymentTerms: f.paymentTerms || undefined, notes: f.notes || undefined, fileUrl: f.fileUrl || undefined,
        lines: lines.filter((l) => l.qty > 0 || l.description || l.customerPart),
        quote,
      });
      onDone(id);
    } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  return (
    <Modal open={open} title="Log a customer PO" onClose={onClose} wide>
      <div className="os-form-grid">
        <label>Customer PO # *<input value={f.poNumber} onChange={(e) => set('poNumber', e.target.value)} placeholder="WCC-8831" /></label>
        <label>Client *<input value={f.clientName} onChange={(e) => set('clientName', e.target.value)} placeholder="West Coast Commerce" /></label>
        <label>Client email<input value={f.clientEmail} onChange={(e) => set('clientEmail', e.target.value)} placeholder="buyer@client.com" /></label>
        <label>Channel<select value={f.channel} onChange={(e) => set('channel', e.target.value)}>
          <option value="manual">Manual upload</option><option value="email">Email attachment</option>
          <option value="portal">Portal</option><option value="salesperson">Salesperson</option><option value="api">API / form</option>
        </select></label>
        <label>Requested date<input type="date" value={f.requestedDate} onChange={(e) => set('requestedDate', e.target.value)} /></label>
        <label>Payment terms<input value={f.paymentTerms} onChange={(e) => set('paymentTerms', e.target.value)} placeholder="Net 30" /></label>
        <label className="os-span2">Ship-to<input value={f.shipTo} onChange={(e) => set('shipTo', e.target.value)} placeholder="Full ship-to address / location" /></label>
        <label className="os-span2">PO file link<input value={f.fileUrl} onChange={(e) => set('fileUrl', e.target.value)} placeholder="Drive / storage URL of the original PO" /></label>
        <label className="os-span2">Notes<input value={f.notes} onChange={(e) => set('notes', e.target.value)} /></label>
      </div>

      <div className="os-sect">Line items <span style={{ color: 'var(--tx3)' }}>· total {usd(total)}</span></div>
      {lines.map((l, i) => (
        <div key={l.id} className="os-line-row">
          <input placeholder="Customer part" value={l.customerPart} onChange={(e) => setL(i, 'customerPart', e.target.value)} />
          <input placeholder="Description" value={l.description} onChange={(e) => setL(i, 'description', e.target.value)} />
          <input placeholder="Qty" type="number" value={l.qty || ''} onChange={(e) => setL(i, 'qty', e.target.value)} />
          <input placeholder="Unit $" type="number" step="0.0001" value={l.unitPrice || ''} onChange={(e) => setL(i, 'unitPrice', e.target.value)} />
          <span className="os-line-total">{usd(l.total)}</span>
          <button className="os-x" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button className="btn sm" onClick={() => setLines((ls) => [...ls, newLine()])}>＋ Add line</button>

      <div className="os-sect">Link approved quote</div>
      <input placeholder="Search quotes (click to load)…" value={qFilter} onFocus={loadQuotes} onChange={(e) => setQFilter(e.target.value)} />
      <div className="os-quote-picks">
        {shownQuotes.map((x) => (
          <button key={x.id} className={`os-pick${quoteId === x.id ? ' on' : ''}`} onClick={() => setQuoteId(quoteId === x.id ? '' : x.id)}>
            <strong>{x.quoteNum}</strong> {x.customerName} <span style={{ color: 'var(--tx3)' }}>{x.status}</span> {x.total ? usd(x.total) : ''}
          </button>
        ))}
        {!quotes.length && <div className="empty" style={{ padding: 8 }}>Click the search box to load recent quotes.</div>}
      </div>

      {err && <div className="os-err">{err}</div>}
      <div className="os-modal-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy || !f.poNumber || !f.clientName} onClick={submit}>{busy ? 'Saving…' : quoteId ? 'Receive + match to quote' : 'Receive PO'}</button>
      </div>
    </Modal>
  );
}

// ── portal import (website-portal PO submissions living on quote docs) ───
interface PortalPO { quote: QuoteDoc; poNumber: string; qty: number; unit: number; total: number; shipTo: string; date: string }

function PortalImport({ open, onClose, existing, onDone }: { open: boolean; onClose: () => void; existing: (CustomerPO & { id: string })[]; onDone: (id: string) => void }) {
  const [cands, setCands] = useState<PortalPO[] | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const scan = async () => {
    setErr('');
    try {
      const snap = await getDocs(query(collection(db, 'quotes'), where('poNumber', '>', ''), limit(50)));
      const taken = new Set(existing.flatMap((p) => [p.portalQuoteDocId || '', `${p.clientName}::${p.poNumber}`.toLowerCase()]));
      const out: PortalPO[] = [];
      for (const d of snap.docs) {
        const qd = { id: d.id, ...(d.data() as Record<string, unknown>) } as QuoteDoc;
        const poNumber = String((qd as Record<string, unknown>).poNumber || '').trim();
        if (!poNumber) continue;
        if (taken.has(d.id) || taken.has(`${quoteClient(qd)}::${poNumber}`.toLowerCase())) continue;
        const raw = qd as Record<string, unknown>;
        const qty = Number(raw.poSelectedQty) || 0;
        const tiers = quoteTiers(qd);
        const tier = tiers.find((t) => t.qty === qty) || tiers[Number(raw.poQtyIndex) || 0];
        out.push({
          quote: qd, poNumber, qty: qty || tier?.qty || 0,
          unit: tier?.ppu || 0,
          total: Number(raw.poSelectedTotal) || tier?.total || 0,
          shipTo: String(raw.poShipTo || ''), date: String(raw.poRequiredDate || ''),
        });
      }
      setCands(out);
    } catch (e) { setErr((e as Error).message); setCands([]); }
  };

  const importOne = async (c: PortalPO) => {
    setBusy(c.quote.id); setErr('');
    try {
      const line: POLine = {
        id: 'p1', customerPart: '', description: String((c.quote.fields as Record<string, unknown> | undefined)?.jobDesc || c.quote.quoteNum || 'Portal PO line'),
        qty: c.qty, unitPrice: c.unit, total: c.total || c.qty * c.unit, mappedTo: '', mapStatus: 'unmapped',
      };
      const id = await receiveCustomerPO({
        poNumber: c.poNumber, clientName: quoteClient(c.quote), clientEmail: quoteEmail(c.quote),
        channel: 'portal', total: c.total || undefined, requestedDate: c.date || undefined, shipTo: c.shipTo || undefined,
        lines: [line], portalQuoteDocId: c.quote.id, quote: c.quote,
      });
      onDone(id);
    } catch (e) { setErr((e as Error).message); }
    setBusy('');
  };

  return (
    <Modal open={open} title="Import POs submitted through the portal" onClose={onClose} wide>
      <div className="page-sub" style={{ marginBottom: 10 }}>Scans OS quotes carrying a client-submitted PO that isn't in the inbox yet.</div>
      {!cands && <button className="btn primary" onClick={scan}>Scan portal quotes</button>}
      {cands && !cands.length && <div className="empty">Nothing to import — every portal PO is already in the inbox.</div>}
      {cands?.map((c) => (
        <div key={c.quote.id} className="os-pick" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 6 }}>
          <span><strong>{c.poNumber}</strong> · {quoteLabel(c.quote)} · qty {c.qty.toLocaleString()} · {usd(c.total)}</span>
          <button className="btn sm primary" disabled={busy === c.quote.id} onClick={() => importOne(c)}>{busy === c.quote.id ? 'Importing…' : 'Import'}</button>
        </div>
      ))}
      {err && <div className="os-err">{err}</div>}
    </Modal>
  );
}
